import {
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AccountListItem } from '@assetaccountant/api/models/account-list-item';
import { GlAccountSubtype } from '@assetaccountant/api/models/gl-account-subtype';
import { GlAccountTypeId } from '@assetaccountant/api/models/gl-account-type-id';
import { CompareFn, SORT_COMPARE } from '@assetaccountant/core/services/compare.factory';
import { filterAndSortByValue } from '@assetaccountant/core/utils/search-utils';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, OperatorFunction, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-gl-account-typeahead',
  templateUrl: './gl-account-typeahead.component.html',
  styleUrls: ['./gl-account-typeahead.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlAccountTypeaheadComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class GlAccountTypeaheadComponent implements OnChanges, ControlValueAccessor {
  readonly GlAccountTypeId = GlAccountTypeId;

  @Input() glAccountSubtype: GlAccountSubtype;
  @Input() externalGlAccounts?: AccountListItem[];

  @ViewChild('typeahead', { static: true }) typeahead: NgbTypeahead;
  @ViewChild('input', { static: true, read: ElementRef }) input: ElementRef<HTMLInputElement>;

  value: string | null = null;
  model: AccountListItem | null = null;
  disabled = false;

  click$ = new Subject<string>();
  focus$ = new Subject<string>();

  private sortedGlAccounts: AccountListItem[];

  private _onChange?: (val: string | null) => void;
  private _onTouched?: () => void;

  compareByAccountType = (a, b) => {
    const aPref = a.accountTypeId === this.glAccountSubtype.parentId;
    const bPref = b.accountTypeId === this.glAccountSubtype.parentId;
    if (aPref === bPref) return 0;
    return aPref ? -1 : 1;
  };

  constructor(@Inject(SORT_COMPARE) private sortCompare: CompareFn) {}

  ngOnChanges() {
    this.sortedGlAccounts = (this.externalGlAccounts ?? []).slice().sort((a, b) => {
      const byAccountType = this.compareByAccountType(a, b);
      if (byAccountType !== 0) {
        return byAccountType;
      }

      const byCode = this.sortCompare(a.code ?? '', b.code ?? '');
      if (byCode !== 0) {
        return byCode;
      }

      return this.sortCompare(a.name, b.name);
    });

    this.updateModel();
  }

  search: OperatorFunction<string, readonly AccountListItem[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(100), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.typeahead?.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map(term =>
        !term
          ? this.sortedGlAccounts
          : filterAndSortByValue(this.sortedGlAccounts, term, 'code', 'name')
              .sort(this.compareByAccountType)
              .slice(0, 10),
      ),
    );
  };

  formatter = (result: AccountListItem) =>
    result.code ? `${result.code}: ${result.name}` : result.name;

  writeValue(obj: string | null): void {
    this.value = obj;

    this.updateModel();
  }

  onSelect($event: NgbTypeaheadSelectItemEvent<AccountListItem>) {
    this.model = $event.item;
    this.value = this.model?.id;

    this._onChange?.(this.value);
  }

  onBlur() {
    this._onTouched?.();

    setTimeout(() => {
      if (!this.model) {
        this.value = null;
        this._onChange?.(this.value);

        if (this.input?.nativeElement) {
          this.input.nativeElement.value = '';
        }
      }
    });
  }

  registerOnChange(fn: (val: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  private updateModel() {
    this.model = this.value
      ? (this.externalGlAccounts?.find(gl => gl.id === this.value) ?? null)
      : null;

    if (!this.model && this.input?.nativeElement) {
      this.input.nativeElement.value = '';
    }
  }
}
